import * as React from 'react';
import { getFlow, updateFlow, forceUpdateFlow, createFlow } from '@flows/data/flowsRepository';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupabaseAuthHeaders } from '@shared/api/supabase-auth-headers';
import { useWorkspace } from '@/context/workspace';
import { Button } from '@/components/ui/button';
import { cn } from '@shared/utils/cn';
import ConflictModal from './ConflictModal';
import type { Edge, Node } from '@xyflow/react';
import { useRunControl } from '@/context/run-control';
import { toFlowGraph } from '@flows/lib/flow-graph-adapter';
import RunToolbar from '@features/flows/ui/RunToolbar';
import { toast } from 'sonner';
import { useCanvasStore } from '@features/flows/state/canvas-store';

function FlowHeaderComponent(props: {
  flowId?: string | null;
  name?: string | null;
  revision?: number | null;
  onGraphReplace?: (graph: { nodes: Node[]; edges: Edge[] }, revision: number, name?: string | null, id?: string) => void;
}) {
  const { flowId: urlFlowId } = useParams<{ flowId?: string }>();
  const navigate = useNavigate();
  const [conflict, setConflict] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const { active: ws } = useWorkspace();
  const { isBusy, status, mode, runId } = useRunControl();
  const readGraphSnapshot = React.useCallback((): { nodes: Node[]; edges: Edge[] } => {
    const { nodes, edges } = useCanvasStore.getState().graph;
    return { nodes, edges };
  }, []);

  const flowId = props.flowId ?? urlFlowId ?? undefined;
  const displayedName = (props.name ?? urlFlowId ?? 'Untitled') as string;
  const currentRevision = props.revision ?? 0;

  const doSave = async () => {
    if (!flowId) return;
    const { nodes, edges } = readGraphSnapshot();
    const graph = toFlowGraph(nodes, edges);
    const patch = { name: inputRef.current?.value ?? displayedName, graph, revision: currentRevision + 1 } as any;
    const auth = await getSupabaseAuthHeaders();
    const withWs = ws?.id ? { ...auth, 'X-Workspace-ID': ws.id } : auth;
    const row = await updateFlow(flowId, patch, currentRevision, withWs);
    if (!row) { setConflict(true); return; }
    if (inputRef.current) inputRef.current.value = row.name ?? inputRef.current.value;
    props.onGraphReplace?.({ nodes, edges }, row.revision, row.name ?? null, flowId);
    try { toast.success('Flow saved'); } catch {}
  };

  const reloadRemote = async () => {
    if (!flowId) return;
    const auth = await getSupabaseAuthHeaders();
    const withWs = ws?.id ? { ...auth, 'X-Workspace-ID': ws.id } : auth;
    const row = await getFlow(flowId, withWs);
    if (row?.graph) {
      props.onGraphReplace?.(row.graph as any, row.revision, row.name ?? null, row.id);
      if (inputRef.current) inputRef.current.value = row.name ?? inputRef.current.value;
    }
    setConflict(false);
  };

  const overwriteRemote = async () => {
    if (!flowId) return;
    const auth = await getSupabaseAuthHeaders();
    const withWs = ws?.id ? { ...auth, 'X-Workspace-ID': ws.id } : auth;
    const { nodes, edges } = readGraphSnapshot();
    const graph = toFlowGraph(nodes, edges);
    const row = await forceUpdateFlow(flowId, { name: inputRef.current?.value ?? displayedName, graph, revision: currentRevision + 1 } as any, withWs);
    if (inputRef.current) inputRef.current.value = row.name ?? inputRef.current.value;
    props.onGraphReplace?.({ nodes, edges }, row.revision, row.name ?? null, flowId);
    setConflict(false);
    try { toast.success('Flow saved'); } catch {}
  };

  const saveAs = async () => {
    const current = inputRef.current?.value ?? displayedName;
    const newName = prompt('Save as… name', `${current || 'Untitled'} Copy`);
    if (!newName) return;
    const auth = await getSupabaseAuthHeaders();
    const withWs = ws?.id ? { ...auth, 'X-Workspace-ID': ws.id } : auth;
    const { nodes, edges } = readGraphSnapshot();
    const graph = toFlowGraph(nodes, edges);
    const row = await createFlow({ name: newName, graph, revision: 1 } as any, withWs);
    if (inputRef.current) inputRef.current.value = row.name ?? newName;
    navigate(`/flows/${encodeURIComponent(row.id)}`);
    try { toast.success('Flow saved'); } catch {}
  };

  // Elapsed timer for centered status label
  const [t0, setT0] = React.useState<number | null>(null);
  const [t1, setT1] = React.useState<number | null>(null);
  const [now, setNow] = React.useState<number>(Date.now());
  const isRunning = status === 'running';
  const isTerminal = status === 'completed' || status === 'failed' || status === 'canceled';
  React.useEffect(() => {
    if (runId && !t0) {
      setT0(Date.now());
      setT1(null);
    }
    if (!runId) {
      setT0(null);
      setT1(null);
    }
  }, [runId, t0]);
  React.useEffect(() => {
    if (t0 && isTerminal && !t1) setT1(Date.now());
    if (isRunning && t1) setT1(null);
  }, [isTerminal, isRunning, t0, t1]);
  React.useEffect(() => {
    if (!t0 || isTerminal) return;
    const h = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(h);
  }, [t0, isTerminal]);
  const elapsedMs = t0 ? Math.max(0, (t1 ?? now) - t0) : 0;
  const fmt = (ms: number) => (ms < 1000 ? `${ms}ms` : `${Math.round(ms / 1000)}s`);

  // Status chip coloring
  const chipClass = React.useMemo(() => {
    const base = 'text-xs px-2 py-1 rounded border';
    switch (status) {
      case 'completed':
        return `${base} bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-400`;
      case 'failed':
      case 'canceled':
        return `${base} bg-red-500/10 border-red-500/40 text-red-700 dark:text-red-400`;
      case 'running':
        return `${base} bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-400`;
      case 'paused':
      case 'starting':
        // Treat paused/starting as waiting-ish
        return `${base} bg-fuchsia-500/10 border-fuchsia-500/40 text-fuchsia-700 dark:text-fuchsia-400`;
      default:
        return `${base} bg-muted/40`;
    }
  }, [status]);

  const exportJson = () => {
    const fileId = flowId ?? 'flow';
    const { nodes, edges } = readGraphSnapshot();
    const graph = toFlowGraph(nodes, edges);
    const doc = { id: flowId, name: inputRef.current?.value ?? displayedName, graph, revision: currentRevision } as any;
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(inputRef.current?.value || fileId || 'flow')}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const importJson = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const doc = JSON.parse(text);
    if (!doc?.graph?.nodes || !doc?.graph?.edges) { alert('Invalid flow JSON'); return; }
    props.onGraphReplace?.(doc.graph as any, currentRevision, doc.name ?? null, flowId);
  };

  return (
    <div className="sticky top-0 z-40 h-12 border-b bg-background/95 backdrop-blur flex items-center gap-2 px-3">
      <input
        key={`${flowId ?? 'local'}:${currentRevision ?? 0}`}
        ref={inputRef}
        className="h-7 rounded border px-2 text-sm"
        defaultValue={displayedName}
        placeholder="Flow name"
        aria-label="Flow name"
      />
      {/* Centered run status label */}
      <div className="absolute left-1/2 -translate-x-1/2">
        <div className={cn(chipClass)}>
          <span className="font-medium">{status ?? '—'}</span>
          <span className="ml-2 text-muted-foreground">{elapsedMs ? fmt(elapsedMs) : ''}</span>
          {mode === 'interactive' && <span className="ml-2 text-muted-foreground">(interactive)</span>}
        </div>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <RunToolbar flowId={flowId} />
        <div className="w-px h-5 bg-border" />
        {flowId && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/flows/${encodeURIComponent(flowId)}/files`)}
          >
            Files
          </Button>
        )}
        {false && (
          <>
            <label className="text-sm underline hover:no-underline cursor-pointer">
              Import
              <input type="file" accept="application/json" className="hidden" onChange={importJson} />
            </label>
            <Button size="sm" variant="outline" onClick={exportJson}>Export</Button>
            <Button size="sm" variant="secondary" onClick={saveAs}>Save as</Button>
          </>
        )}
        <Button size="sm" onClick={doSave}>Save</Button>
      </div>
      <ConflictModal open={conflict} onReload={reloadRemote} onOverwrite={overwriteRemote} onCancel={() => setConflict(false)} />
    </div>
  );
}

type FlowHeaderProps = Parameters<typeof FlowHeaderComponent>[0];

const arePropsEqual = (prev: FlowHeaderProps, next: FlowHeaderProps) => (
  prev.flowId === next.flowId &&
  prev.name === next.name &&
  prev.revision === next.revision &&
  prev.onGraphReplace === next.onGraphReplace
);

export { FlowHeaderComponent as FlowHeader };
export default React.memo(FlowHeaderComponent, arePropsEqual);
